import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Download, FileSpreadsheet, TrendingUp, TrendingDown, Receipt, Package, Wallet, ArrowRight, CalendarDays, FileText, ChevronDown, ChevronRight } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL;
const TVA_RATE = 8.1;
const TVA_CODE = 'VM81';

const AccountingExportPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const now = new Date();
  const yearStart = `${now.getFullYear()}-01-01`;
  const today = now.toISOString().split('T')[0];

  const [dateFrom, setDateFrom] = useState(yearStart);
  const [dateTo, setDateTo] = useState(today);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingBexio, setExportingBexio] = useState(false);
  const [showAccounts, setShowAccounts] = useState(false);
  const [accounts, setAccounts] = useState(null);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/accounting/summary?date_from=${dateFrom}&date_to=${dateTo}`);
      setSummary(res.data);
    } catch {
      toast.error("Erreur chargement du resume comptable");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSummary(); }, [dateFrom, dateTo]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/api/accounting/export?date_from=${dateFrom}&date_to=${dateTo}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Export_Comptable_${dateFrom}_au_${dateTo}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export XLSX telecharge');
    } catch {
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  };

  const handleExportBexio = async () => {
    setExportingBexio(true);
    try {
      const res = await axios.get(`${API}/api/accounting/export-bexio?date_from=${dateFrom}&date_to=${dateTo}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Bexio_Journal_${dateFrom}_au_${dateTo}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export Bexio CSV telecharge');
    } catch {
      toast.error("Erreur lors de l'export Bexio");
    } finally {
      setExportingBexio(false);
    }
  };

  const loadAccounts = async () => {
    if (accounts) { setShowAccounts(!showAccounts); return; }
    try {
      const res = await axios.get(`${API}/api/accounting/accounts`);
      setAccounts(res.data);
      setShowAccounts(true);
    } catch { toast.error('Erreur chargement plan comptable'); }
  };

  const StatCard = ({ icon: Icon, label, value, sub, color, testid }) => (
    <Card className="p-4 hover:shadow-md transition-shadow" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className="text-lg font-bold text-slate-900">{typeof value === 'number' ? value.toLocaleString('fr-CH', {minimumFractionDigits: 2}) : value} CHF</p>
          {sub && <p className="text-xs text-slate-400">{sub}</p>}
        </div>
      </div>
    </Card>
  );

  const net = summary?.net_result || 0;
  const isPositive = net >= 0;

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
    <div className="space-y-6" data-testid="accounting-export-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('accounting.accounting_export')}</h1>
          <p className="text-sm text-slate-500 mt-1">Exportez vos donnees financieres pour votre fiduciaire</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleExportBexio}
            disabled={exportingBexio}
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            data-testid="export-bexio-btn"
          >
            <FileText className="w-4 h-4" />
            {exportingBexio ? 'Export...' : 'Export Bexio CSV'}
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
            data-testid="export-xlsx-btn"
          >
            <FileSpreadsheet className="w-4 h-4" />
            {exporting ? 'Export...' : 'Telecharger XLSX'}
          </Button>
        </div>
      </div>

      {/* Date filter */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row items-end gap-4">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-slate-400" />
            <Label className="text-sm text-slate-600 whitespace-nowrap">Periode :</Label>
          </div>
          <div className="flex-1">
            <Label className="text-xs text-slate-400">Du</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="date-from" />
          </div>
          <div className="flex items-center text-slate-300"><ArrowRight className="w-4 h-4" /></div>
          <div className="flex-1">
            <Label className="text-xs text-slate-400">Au</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="date-to" />
          </div>
          <Button variant="outline" size="sm" onClick={() => { setDateFrom(yearStart); setDateTo(today); }} data-testid="reset-dates-btn">
            Annee en cours
          </Button>
        </div>
      </Card>

      {loading ? (
        <div className="text-center py-12 text-slate-400">{t('common.loading')}</div>
      ) : summary && (
        <>
          {/* Net result banner */}
          <Card className={`p-6 ${isPositive ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid="net-result-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isPositive ? <TrendingUp className="w-8 h-8 text-emerald-600" /> : <TrendingDown className="w-8 h-8 text-red-600" />}
                <div>
                  <p className="text-sm text-slate-600">Resultat net</p>
                  <p className={`text-3xl font-bold ${isPositive ? 'text-emerald-700' : 'text-red-700'}`}>
                    {net.toLocaleString('fr-CH', {minimumFractionDigits: 2})} CHF
                  </p>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Du {new Date(dateFrom).toLocaleDateString('fr-CH')} au {new Date(dateTo).toLocaleDateString('fr-CH')}
              </p>
            </div>
          </Card>

          {/* Revenue cards */}
          <div>
            <h2 className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wider">Revenus</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard icon={Receipt} label={`Factures (${summary.invoices_count})`} value={summary.invoices_total} sub={`dont ${summary.invoices_paid.toLocaleString('fr-CH', {minimumFractionDigits: 2})} CHF payees`} color="bg-blue-600" testid="stat-invoices" />
              <StatCard icon={Wallet} label={`Factures atelier (${summary.wo_invoices_count})`} value={summary.wo_invoices_total} color="bg-indigo-600" testid="stat-wo-invoices" />
              <StatCard icon={FileSpreadsheet} label={`Devis (${summary.quotes_count})`} value={summary.quotes_total} color="bg-slate-600" testid="stat-quotes" />
            </div>
          </div>

          {/* Expense cards */}
          <div>
            <h2 className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wider">Depenses</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StatCard icon={Package} label={`Depenses generales (${summary.expenses_count})`} value={summary.expenses_total} color="bg-amber-600" testid="stat-expenses" />
              <StatCard icon={Download} label={`Factures fournisseurs (${summary.supplier_invoices_count})`} value={summary.supplier_invoices_total} color="bg-red-600" testid="stat-suppliers" />
            </div>
          </div>

          {/* Info about the export content */}
          <Card className="p-4 bg-slate-50 border-slate-200">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Contenu de l'export XLSX</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-slate-600">
              <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-600"></span> Factures (systeme)</div>
              <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-indigo-600"></span> Factures atelier</div>
              <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-600"></span> Devis</div>
              <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-600"></span> Depenses generales</div>
              <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-600"></span> Factures fournisseurs</div>
              <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-600"></span> Resume global</div>
            </div>
          </Card>

          {/* Bexio CSV info */}
          <Card className="p-4 bg-blue-50 border-blue-200">
            <h3 className="text-sm font-semibold text-blue-700 mb-2">Export Bexio CSV</h3>
            <p className="text-xs text-blue-600 mb-3">
              Genere un fichier CSV d'ecritures comptables (journal) compatible avec l'import Bexio via Accrio.
              Colonnes : Date, Compte debit, Compte credit, Montant, Code TVA, Description, Reference.
              Taux TVA : {TVA_RATE}% (code {TVA_CODE}).
            </p>
            <button onClick={loadAccounts} className="flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900 transition-colors" data-testid="show-accounts-btn">
              {showAccounts ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              Voir le plan comptable utilise
            </button>
            {showAccounts && accounts && (
              <div className="mt-3 space-y-3">
                {['Bilan', 'Produits', 'Charges'].map(cat => (
                  <div key={cat}>
                    <p className="text-[11px] font-bold text-blue-800 uppercase tracking-wider mb-1">{cat}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {accounts.accounts.filter(a => a.category === cat).map(a => (
                        <div key={a.number} className="flex items-center gap-2 text-xs text-blue-700 bg-blue-100/50 rounded px-2 py-1">
                          <span className="font-mono font-bold w-10">{a.number}</span>
                          <span>{a.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <div>
                  <p className="text-[11px] font-bold text-blue-800 uppercase tracking-wider mb-1">Affectation des depenses</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                    {Object.entries(accounts.expense_mapping).map(([cat, info]) => (
                      <div key={cat} className="flex items-center gap-2 text-xs text-blue-700 bg-blue-100/50 rounded px-2 py-1">
                        <span className="capitalize w-20">{cat}</span>
                        <span className="text-blue-400">→</span>
                        <span className="font-mono font-bold">{info.account}</span>
                        <span className="text-blue-500">{info.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
    </AdminLayout>
  );
};

export default AccountingExportPage;
